import { createContext, useEffect, useState, PropsWithChildren } from 'react';

import ScheduledLesson, {
  ScheduledLessonTypeEnum,
} from '@/models/ScheduledLesson';
import LessonProgress from '@/models/LessonProgress';
import Lesson from '@/models/Lesson';
import { isStudent } from '@/functions/auth';
import useAuth from '../hook/useAuth';
import useStudentContext from '../hook/useStudentContext';
import {
  lessonsProgressesQueryKeys,
  lessonsQueryKeys,
  resourceProgressQuerykeys,
  scheduledLessonsQueryKeys,
} from '../services/querykeys';
import useInfiniteService from '../hook/useInfiniteService';
import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import useListService from '../hook/useListService';
import { enrollmentValidation } from '@/utils/enrollmentValidation';
import { ResourceProgress } from '@/models/ResourceProgress';
import Subchapter from '@/models/Subchapter';
import Chapter from '@/models/Chapter';
import { ResultsTypeError } from '@/errors/jsonApiResponse';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { isBlockedLesson } from '@/utils/isBlockedLesson';
interface LessonsPaginate {
  prev?: Pick<ScheduledLesson, 'lesson' | 'type'>;
  next?: Pick<ScheduledLesson, 'lesson' | 'type'>;
}

export interface LessonContextProps {
  scheduledLesson?: ScheduledLesson;
  lessonProgress?: LessonProgress;
  lessonsError?: unknown;
  updateLessonProgress(): Promise<void>;
  pagination: LessonsPaginate;
  loading?: boolean;
  setParams: (params: LessonContextParams) => void;
  fullScreen: boolean;
  setFullscreen: (full: boolean) => void;
  lesson?: Lesson;
  loadingLesson?: boolean;
  resourceProgress?: ResourceProgress[];
  subchapter?: Subchapter;
  setChapter: (chapter?: Chapter) => void;
  setSubchapter: (subchapter?: Subchapter) => void;

  chapter?: Chapter;
}

const LessonContext = createContext<LessonContextProps>(
  {} as LessonContextProps,
);

interface LessonContextParams {
  slugCourseName?: string;
  lessonId?: string;
  klassId?: string;
}

export function LessonProvider({ children }: PropsWithChildren) {
  const [{ slugCourseName = '', lessonId, klassId }, setParams] =
    useState<LessonContextParams>({});

  const [fullScreen, setFullscreen] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    nextLesson,
    updateProgress,
    currentProgress,
    klass,
    updateLessonProgressDashboardStats,
  } = useStudentContext();

  const validEnrollment = enrollmentValidation(
    currentProgress?.enrollmentStatus,
  );

  const studentView = !!user && isStudent(user?.userType);

  const hasKlassId = !!klassId && !isNaN(Number(klassId));

  const hasLessonId = !!lessonId && !isNaN(Number(lessonId));

  const enabledLessons = hasLessonId && !hasKlassId;

  const [subchapter, setSubchapter] = useState<Subchapter>();

  const [chapter, setChapter] = useState<Chapter>();

  const {
    results: lessons,
    error: lessonsError,
    isInitialLoading: loadingLessons,
    hasNextPage: hasNextLessonsPage,
    fetchNextPage: fetchNextLessonsPage,
  } = useInfiniteService({
    ...lessonsQueryKeys.nestedList(slugCourseName ?? '')._ctx.infinity,
    enabled: enabledLessons,
  });

  const enabledLessonProgress =
    studentView && hasLessonId && !!currentProgress?.id;

  const convertedLessonId = Number(lessonId);
  const lessonIds = isNaN(convertedLessonId) ? undefined : [convertedLessonId];
  const {
    results: lessonProgress,
    isInitialLoading: loadingLessonProgress,
    invalidate: invalidateLessonProgress,
  } = useListService({
    ...lessonsProgressesQueryKeys.list({
      lesson: lessonIds,
      courseProgressId: [currentProgress?.id ?? 0],
    }),
    staleTime,
    enabled: enabledLessonProgress,
  });

  const currentLessonProgress = getCurrentLessonProgress(lessonProgress);

  const enabledScheduledLessons = hasKlassId && (!studentView || !!klass);

  const {
    results: scheduledLessons,
    isInitialLoading: loadingScheduledLessons,
    hasNextPage: hasNextScheduledLessonsPage,
    fetchNextPage: fetchScheduledLessonsNextPage,
  } = useInfiniteService({
    ...scheduledLessonsQueryKeys.list({ klassId: Number(klassId) })._ctx
      .infinity,
    enabled: enabledScheduledLessons,
  });

  const currentLessons = scheduledLessons.length
    ? scheduledLessons.map(({ lesson, type }) => ({ lesson, type }))
    : lessons.map(lesson => ({
        lesson,
        type: ScheduledLessonTypeEnum.NORMAL,
      }));

  const lessonIndex = currentLessons?.findIndex(
    ({ lesson }) => lesson.id === Number(lessonId),
  );

  const getPagination = (): LessonsPaginate => {
    if (lessonIndex === undefined || !currentLessons) return {};

    const prev =
      lessonIndex <= 0 ? undefined : currentLessons.at(lessonIndex - 1);
    const next = currentLessons.at(lessonIndex + 1);

    const nextScheduledLesson = scheduledLessons.at(lessonIndex + 1);

    const blockLesson =
      studentView &&
      isBlockedLesson({
        datetime: nextScheduledLesson?.datetime,
        lessonId: nextScheduledLesson?.lesson.id,
        nextLesson,
        hasDone: nextScheduledLesson?.hasDone,
        presence: currentLessonProgress?.scheduledLessonReport?.presence,
      });

    const shouldBlockNextLesson = validEnrollment ? blockLesson : false;

    return {
      prev,
      next: shouldBlockNextLesson ? undefined : next,
    };
  };

  const updateLessonProgress = async () => {
    if (!currentLessonProgress) throw new Error('lesson progress not found');

    await invalidateLessonProgress();

    await updateProgress();

    await updateLessonProgressDashboardStats(currentLessonProgress.id);

    await queryClient.invalidateQueries({
      queryKey: lessonsProgressesQueryKeys.list({
        lesson: lessonIds,
      }).queryKey,
    });

    await queryClient.invalidateQueries({
      queryKey: lessonsProgressesQueryKeys.list({
        lesson: lessonIds,
        pageSize: 1,
      }).queryKey,
    });
  };

  const scheduledLesson =
    lessonIndex !== undefined ? scheduledLessons?.[lessonIndex] : undefined;

  const lesson = lessonIndex !== undefined ? lessons?.[lessonIndex] : undefined;

  const resources =
    scheduledLesson?.lesson.resources ||
    lesson?.resources ||
    currentLessonProgress?.lesson.resources;

  const { results: resourceProgress } = useListService({
    ...resourceProgressQuerykeys.list({
      resourceId: lesson?.resources,
    }),
    enabled: !!resources?.length && studentView,
  });

  const loading =
    loadingLessons || loadingLessonProgress || loadingScheduledLessons;

  useEffect(() => {
    if (hasNextScheduledLessonsPage) fetchScheduledLessonsNextPage();
  }, [fetchScheduledLessonsNextPage, hasNextScheduledLessonsPage]);

  useEffect(() => {
    if (hasNextLessonsPage) fetchNextLessonsPage();
  }, [fetchNextLessonsPage, hasNextLessonsPage]);

  useEffect(() => {
    setFullscreen(false);
  }, [lesson?.id]);

  const values: LessonContextProps = {
    scheduledLesson: scheduledLesson,
    lessonProgress: currentLessonProgress,
    pagination: getPagination(),
    lesson: scheduledLesson?.lesson || lesson || currentLessonProgress?.lesson,
    lessonsError,
    loading,
    updateLessonProgress,
    setParams,
    fullScreen,
    setFullscreen,
    resourceProgress,
    subchapter,
    setSubchapter,
    chapter,
    setChapter,
  };

  return (
    <LessonContext.Provider value={values}>{children}</LessonContext.Provider>
  );
}

export default LessonContext;

const getCurrentLessonProgress = (lessonProgresses: LessonProgress[]) => {
  try {
    return lessonProgresses.at(0);
  } catch {
    const error = new ResultsTypeError();
    captureException(error.getError(), {
      extra: {
        response: lessonProgresses,
        typeof: typeof lessonProgresses,
      },
    });
  }
  return undefined;
};
